
import { useState } from 'react';
import { Eye, EyeOff, KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { changePassword } from '@/services/auth.service';
import type { AccountSettingsForm } from '@/lib/painel-account-settings';
import {
  getPasswordStrength,
  PASSWORD_STRENGTH_COLORS,
  PASSWORD_STRENGTH_LABELS,
  PASSWORD_STRENGTH_WIDTH,
  validatePasswordChange,
} from '@/lib/painel-account-settings';
import {
  painelButtonPrimaryClass,
  painelButtonOutlineClass,
} from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';
import { AccountField, AccountSettingsCard, accountInputClass } from './account-settings-shared';

type SecurityPatch = Pick<
  AccountSettingsForm,
  'currentPassword' | 'newPassword' | 'confirmPassword'
>;

type SecurityCardProps = SecurityPatch & {
  onChange: (patch: Partial<SecurityPatch>) => void;
  delay?: number;
};

function PasswordInput({
  id,
  value,
  onChange,
  placeholder,
  autoComplete,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
}) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(accountInputClass, 'pr-11')}
        placeholder={placeholder}
        autoComplete={autoComplete}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className={cn(
          'absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate-400',
          'transition-colors hover:bg-white/10 hover:text-slate-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/30'
        )}
        aria-label={visible ? 'Esconder senha' : 'Mostrar senha'}
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export function SecurityCard({
  currentPassword,
  newPassword,
  confirmPassword,
  onChange,
  delay = 0,
}: SecurityCardProps) {
  const [isChanging, setIsChanging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const strength = getPasswordStrength(newPassword);
  const validationError = validatePasswordChange({ newPassword, confirmPassword });

  const handleChangePassword = async () => {
    if (!newPassword && !confirmPassword) return;
    const err = validatePasswordChange({ newPassword, confirmPassword });
    if (err) {
      setLocalError(err);
      return;
    }
    if (!currentPassword) {
      setLocalError('Informe a sua senha atual para confirmar a alteração.');
      return;
    }
    setLocalError(null);
    setIsChanging(true);
    try {
      await changePassword(currentPassword, newPassword);
      onChange({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Senha alterada', {
        description: 'A sua nova senha já está ativa. Use-a no próximo login.',
      });
    } catch (e) {
      setLocalError(
        e instanceof Error ? e.message : 'Não foi possível alterar a senha. Tente novamente.'
      );
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <AccountSettingsCard
      title="Segurança"
      description="Mantenha sua conta protegida com uma senha forte e atualizada."
      icon={ShieldCheck}
      accentClass="bg-rose-500/15 text-rose-300 ring-rose-400/25"
      delay={delay}
    >
      <div className="grid max-w-xl gap-5">
        <AccountField id="account-current-password" label="Senha atual">
          <PasswordInput
            id="account-current-password"
            value={currentPassword}
            onChange={(v) => onChange({ currentPassword: v })}
            placeholder="••••••••"
            autoComplete="current-password"
          />
        </AccountField>

        <AccountField id="account-new-password" label="Nova senha">
          <PasswordInput
            id="account-new-password"
            value={newPassword}
            onChange={(v) => {
              onChange({ newPassword: v });
              setLocalError(null);
            }}
            placeholder="Mínimo 8 caracteres"
            autoComplete="new-password"
          />
        </AccountField>

        {newPassword ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <span className="font-inter text-xs text-slate-400">Força da senha</span>
              <span
                className={cn(
                  'font-inter text-xs font-semibold',
                  strength === 'very_weak' || strength === 'weak'
                    ? 'text-rose-300'
                    : strength === 'good'
                      ? 'text-amber-200'
                      : strength === 'strong'
                        ? 'text-sky-200'
                        : 'text-emerald-300'
                )}
              >
                {PASSWORD_STRENGTH_LABELS[strength]}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-300',
                  PASSWORD_STRENGTH_COLORS[strength],
                  PASSWORD_STRENGTH_WIDTH[strength]
                )}
              />
            </div>
          </div>
        ) : null}

        <AccountField id="account-confirm-password" label="Confirmar nova senha">
          <PasswordInput
            id="account-confirm-password"
            value={confirmPassword}
            onChange={(v) => {
              onChange({ confirmPassword: v });
              setLocalError(null);
            }}
            placeholder="Repita a nova senha"
            autoComplete="new-password"
          />
        </AccountField>

        {localError || validationError ? (
          <p className="font-inter text-xs text-rose-300" role="alert">
            {localError ?? validationError}
          </p>
        ) : null}

        <div>
          <Button
            type="button"
            className={cn(painelButtonPrimaryClass, 'h-10 px-5')}
            onClick={handleChangePassword}
            disabled={isChanging || !newPassword}
          >
            {isChanging ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Alterando…
              </>
            ) : (
              <>
                <KeyRound className="mr-2 h-4 w-4" />
                Alterar senha
              </>
            )}
          </Button>
        </div>
      </div>
    </AccountSettingsCard>
  );
}
